"use client"

import * as React from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@workspace/ui/components/shadcn/card"
import { Input } from "@workspace/ui/components/shadcn/input"
import { Label } from "@workspace/ui/components/shadcn/label"
import { Button } from "@workspace/ui/components/shadcn/button"
import { Checkbox } from "@workspace/ui/components/shadcn/checkbox"
import { Separator } from "@workspace/ui/components/shadcn/separator"

import { Eye, EyeOff, Github, Facebook } from "lucide-react"

const formSchema = z.object({
    email: z.string().min(1, "E-Mail wird benötigt").email("Ungültige E-Mail-Adresse"),
    password: z.string().min(12, "Passwort muss mindestens 12 Zeichen haben"),
    remember: z.boolean().default(false),
})

export type LoginFormValues = z.infer<typeof formSchema>

export type LoginFormProps = {
    registrationHref?: string
    forgotPasswordHref?: string
}

export function LoginForm({
    registrationHref = "/registrieren",
    forgotPasswordHref = "/passwort-vergessen",
}: LoginFormProps) {
    const [showPassword, setShowPassword] = React.useState(false)
    const [loading, setLoading] = React.useState(false)
    const [errorMessage, setErrorMessage] = React.useState("")

    const form = useForm<LoginFormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
            password: "",
            remember: false,
        },
    })

    const handleSubmit = form.handleSubmit(async (values) => {
        setErrorMessage("")
        setLoading(true)

        try {
            const res = await fetch("/api/auth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    email: values.email,
                    password: values.password,
                    remember: values.remember
                })
            })

            if (!res.ok) {

                setErrorGlobal(err.error)
                setLoading(false)
                return
            }

            window.location.href = "/dashboard"

        } catch (e) {
            setErrorMessage("Server nicht erreichbar")
        } finally {
            setLoading(false)
        }
    })

    return (
        <div className="flex min-h-screen w-full items-center justify-center px-8">
            <Card className="w-full max-w-sm ">
                <CardHeader>
                    <CardTitle className="text-xl">Anmelden</CardTitle>
                    <CardDescription>Greife auf dein Konto zu</CardDescription>
                </CardHeader>

                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">

                        <div className="space-y-2">
                            <Label htmlFor="login_email">E-Mail</Label>
                            <Input
                                id="login_email"
                                name="login_email"
                                type="email"
                                inputMode="email"
                                placeholder="du@example.com"
                                autoComplete="off"
                                {...form.register("email")}
                                disabled={loading}
                            />
                            {form.formState.errors.email && (
                                <p className="text-sm text-red-500">
                                    {form.formState.errors.email.message}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="login_pass">Passwort</Label>
                            <div className="relative">
                                <Input
                                    id="login_pass"
                                    name="login_pass"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••••••"
                                    autoComplete="off"
                                    {...form.register("password")}
                                    disabled={loading}
                                    className="pr-10"
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>

                            {form.formState.errors.password && (
                                <p className="text-sm text-red-500">
                                    {form.formState.errors.password.message}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <Checkbox {...form.register("remember")} disabled={loading} />
                                <span className="text-sm">Angemeldet bleiben</span>
                            </label>

                            <a href={forgotPasswordHref} className="text-sm underline hover:text-primary">
                                Passwort vergessen?
                            </a>
                        </div>

                        {errorMessage && (
                            <p className="text-sm text-red-500 font-medium">{errorMessage}</p>
                        )}

                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? "Laden..." : "Anmelden"}
                        </Button>
                    </form>

                    <div className="mt-6">
                        <Separator className="my-4" />
                        <div className="flex flex-col gap-2">
                            <Button variant="outline" className="w-full" disabled={loading}>
                                <Github className="mr-2 h-4 w-4" /> Mit GitHub anmelden
                            </Button>
                            <Button variant="outline" className="w-full" disabled={loading}>
                                <Facebook className="mr-2 h-4 w-4" /> Mit Facebook anmelden
                            </Button>
                        </div>
                    </div>
                </CardContent>

                <CardFooter className="flex flex-col space-y-2 text-center">
                    <p className="text-sm text-muted-foreground">
                        Noch kein Konto?{" "}
                        <a href={registrationHref} className="underline hover:text-primary">
                            Registrieren
                        </a>
                    </p>
                </CardFooter>
            </Card>
        </div>
    )
}
